import React, { useState, useEffect } from 'react';
import { Search, Filter, Download, ChevronDown, ChevronUp, X, User, Building2, FileText, Shield, Clock, AlertCircle, Info, XCircle } from 'lucide-react';
import { auditLogService } from '../../../services/auditLogService';
import Header from '../../../components/ui/Header';
import Sidebar from '../../../components/ui/Sidebar';

const AuditLoggingSystem = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [auditLogs, setAuditLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    eventType: 'all',
    actorEmail: '',
    company: 'all',
    resource: 'all',
    severity: 'all'
  });

  const eventTypes = [
    { value: 'all', label: 'All Events' },
    { value: 'CRUD', label: 'CRUD Operations' },
    { value: 'AUTH', label: 'Authentication' },
    { value: 'OWNERSHIP', label: 'Ownership Changes' },
    { value: 'BILLING', label: 'Billing Events' },
    { value: 'PERMISSION', label: 'Permission Changes' },
    { value: 'SYSTEM', label: 'System Events' }
  ];

  const resourceTypes = [
    { value: 'all', label: 'All Resources' },
    { value: 'job', label: 'Jobs' },
    { value: 'job_instance', label: 'Job Instances' },
    { value: 'user', label: 'Users' },
    { value: 'company', label: 'Companies' },
    { value: 'timesheet', label: 'Timesheets' },
    { value: 'storage', label: 'Storage' }
  ];

  const severityLevels = [
    { value: 'all', label: 'All Severities' },
    { value: 'info', label: 'Info' },
    { value: 'warning', label: 'Warning' },
    { value: 'error', label: 'Error' },
    { value: 'critical', label: 'Critical' }
  ];

  useEffect(() => {
    loadAuditLogs();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, searchTerm, auditLogs]);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      setError('');

      const data = await auditLogService?.getAll({
        dateFrom: filters?.dateFrom,
        dateTo: filters?.dateTo,
        entityType: filters?.resource,
        actorEmail: filters?.actorEmail
      });

      setAuditLogs(data);
      setFilteredLogs(data);
    } catch (err) {
      console.error('Error loading audit logs:', err);
      setError(err?.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...auditLogs];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered?.filter(log =>
        log?.actorEmail?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        log?.action?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        log?.resourceType?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        log?.company?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }

    // Apply event type filter
    if (filters?.eventType !== 'all') {
      filtered = filtered?.filter(log => log?.eventCategory === filters?.eventType);
    }

    // Apply company filter (if not 'all')
    if (filters?.company !== 'all') {
      filtered = filtered?.filter(log => log?.companyId === filters?.company);
    }

    // Apply severity filter
    if (filters?.severity !== 'all') {
      filtered = filtered?.filter(log => log?.severity === filters?.severity);
    }

    setFilteredLogs(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      eventType: 'all',
      actorEmail: '',
      company: 'all',
      resource: 'all',
      severity: 'all'
    });
    setSearchTerm('');
  };

  const exportLogs = async (format) => {
    try {
      const exportData = await auditLogService?.exportLogs(filteredLogs, format?.toLowerCase());

      // Create download
      const blob = new Blob([exportData], {
        type: format === 'CSV' ? 'text/csv' : 'application/json'
      });
      const url = window.URL?.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date()?.toISOString()}.${format?.toLowerCase()}`;
      document.body?.appendChild(a);
      a?.click();
      document.body?.removeChild(a);
      window.URL?.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting logs:', err);
      alert(`Failed to export logs: ${err?.message}`);
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'info': return <Info className="w-4 h-4 text-blue-400" />;
      case 'warning': return <AlertCircle className="w-4 h-4 text-yellow-400" />;
      case 'error': return <XCircle className="w-4 h-4 text-red-400" />;
      case 'critical': return <XCircle className="w-4 h-4 text-red-300" />;
      default: return <Info className="w-4 h-4 text-text-secondary" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'info': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'warning': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'error': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'critical': return 'bg-red-600/30 text-red-300 border-red-600/40';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getHumanReadableMessage = (log) => {
    const action = log?.action?.toLowerCase();
    const entity = log?.resourceType?.toLowerCase() || log?.entityType?.toLowerCase();
    const actorName = log?.actorName || log?.actorEmail?.split('@')[0] || 'User';

    if (action === 'create') return `${actorName} created a new ${entity}`;
    if (action === 'update') return `${actorName} updated ${entity} settings`;
    if (action === 'delete') return `${actorName} deleted a ${entity}`;
    if (action === 'login') return `${actorName} signed in to the system`;
    if (action === 'logout') return `${actorName} signed out`;
    if (action === 'invite') return `${actorName} invited a new user`;
    if (action === 'suspend') return `${actorName} suspended a ${entity}`;
    if (action === 'activate') return `${actorName} activated a ${entity}`;
    if (action === 'assign') return `${actorName} assigned a ${entity}`;
    if (action === 'complete') return `${actorName} completed a ${entity}`;

    return `${actorName} performed ${action} on ${entity}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-charcoal flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-orange mx-auto"></div>
          <p className="mt-4 text-white-shade-1">Loading audit logs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-dark-charcoal flex items-center justify-center">
        <div className="text-center max-w-md">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Error Loading Logs</h2>
          <p className="text-white-shade-2 mb-4">{error}</p>
          <button
            onClick={loadAuditLogs}
            className="px-6 py-2 bg-primary-orange hover:bg-primary-orange/90 text-white rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <Header />

      {/* Sidebar */}
      <Sidebar />

      {/* Main Content - with margin for fixed header and sidebar */}
      <main className="ml-0 lg:ml-60 mt-16">
        <div className="bg-surface-charcoal border-b border-charcoal-shade-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white">Logs</h1>
              <p className="mt-1 text-sm text-white-shade-2">
                Comprehensive tracking and analysis of all platform activities
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => exportLogs('CSV')}
                className="flex items-center gap-2 px-4 py-2 bg-surface-charcoal hover:bg-charcoal-shade-2 text-white-shade-1 border border-charcoal-shade-2 rounded-lg transition-colors"
              >
                <Download className="w-4 h-4" />
                <span className="font-medium">CSV</span>
              </button>
              <button
                onClick={() => exportLogs('JSON')}
                className="flex items-center gap-2 px-4 py-2 bg-surface-charcoal hover:bg-charcoal-shade-2 text-white-shade-1 border border-charcoal-shade-2 rounded-lg transition-colors"
              >
                <Download className="w-4 h-4" />
                <span className="font-medium">JSON</span>
              </button>
              <button
                onClick={() => exportLogs('PDF')}
                className="flex items-center gap-2 px-4 py-2 bg-primary-orange hover:bg-primary-orange/90 text-white rounded-lg transition-colors"
              >
                <Download className="w-4 h-4" />
                <span className="font-medium">PDF</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filter Bar */}
        <div className="bg-surface-charcoal rounded-lg shadow-sm p-6 mb-6 border border-charcoal-shade-2">
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white-shade-2" />
              <input
                type="text"
                placeholder="Search logs by actor, action, resource, or company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 bg-dark-charcoal border border-charcoal-shade-2 rounded-lg text-white placeholder-white-shade-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-surface-charcoal hover:bg-charcoal-shade-2 text-white-shade-1 border border-charcoal-shade-2 rounded-lg transition-colors"
            >
              <Filter className="w-4 h-4" />
              <span className="font-medium">Filters</span>
              {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-charcoal-shade-2">
              <div>
                <label className="block text-sm font-medium text-white-shade-1 mb-2">Date From</label>
                <input
                  type="date"
                  value={filters?.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
                  className="w-full px-3 py-2 bg-dark-charcoal border border-charcoal-shade-2 rounded-lg text-white focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white-shade-1 mb-2">Date To</label>
                <input
                  type="date"
                  value={filters?.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
                  className="w-full px-3 py-2 bg-dark-charcoal border border-charcoal-shade-2 rounded-lg text-white focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white-shade-1 mb-2">Event Type</label>
                <select
                  value={filters?.eventType}
                  onChange={(e) => handleFilterChange('eventType', e?.target?.value)}
                  className="w-full px-3 py-2 bg-dark-charcoal border border-charcoal-shade-2 rounded-lg text-white focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                >
                  {eventTypes?.map(type => (
                    <option key={type?.value} value={type?.value}>{type?.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-white-shade-1 mb-2">Resource Type</label>
                <select
                  value={filters?.resource}
                  onChange={(e) => handleFilterChange('resource', e?.target?.value)}
                  className="w-full px-3 py-2 bg-dark-charcoal border border-charcoal-shade-2 rounded-lg text-white focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                >
                  {resourceTypes?.map(type => (
                    <option key={type?.value} value={type?.value}>{type?.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-white-shade-1 mb-2">Actor Email</label>
                <input
                  type="text"
                  placeholder="Filter by email..."
                  value={filters?.actorEmail}
                  onChange={(e) => handleFilterChange('actorEmail', e?.target?.value)}
                  className="w-full px-3 py-2 bg-dark-charcoal border border-charcoal-shade-2 rounded-lg text-white placeholder-white-shade-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white-shade-1 mb-2">Severity</label>
                <select
                  value={filters?.severity}
                  onChange={(e) => handleFilterChange('severity', e?.target?.value)}
                  className="w-full px-3 py-2 bg-dark-charcoal border border-charcoal-shade-2 rounded-lg text-white focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                >
                  {severityLevels?.map(level => (
                    <option key={level?.value} value={level?.value}>{level?.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-surface-charcoal hover:bg-charcoal-shade-2 text-white-shade-1 border border-charcoal-shade-2 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                  <span className="font-medium">Clear Filters</span>
                </button>
              </div>
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-charcoal-shade-2">
            <p className="text-sm text-white-shade-2">
              Showing <span className="font-semibold text-white">{filteredLogs?.length}</span> of{' '}
              <span className="font-semibold text-white">{auditLogs?.length}</span> logs
            </p>
          </div>
        </div>

        {/* Audit Logs Table */}
        <div className="bg-surface-charcoal rounded-lg shadow-sm border border-charcoal-shade-2 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-charcoal-shade-2">
              <thead className="bg-dark-charcoal">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white-shade-2 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white-shade-2 uppercase tracking-wider">
                    Activity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white-shade-2 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white-shade-2 uppercase tracking-wider">
                    Severity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white-shade-2 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-surface-charcoal divide-y divide-charcoal-shade-2">
                {filteredLogs?.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-white-shade-2">
                      No logs found
                    </td>
                  </tr>
                ) : (
                  filteredLogs?.map((log) => (
                    <tr key={log?.id} className="hover:bg-charcoal-shade-1 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-white-shade-2" />
                          <span className="text-sm text-white">{formatTimestamp(log?.timestamp)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white">{getHumanReadableMessage(log)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {log?.company ? (
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-white-shade-2" />
                            <span className="text-sm text-white">{log?.company}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-white-shade-2">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${getSeverityColor(log?.severity)}`}>
                          {getSeverityIcon(log?.severity)}
                          <span className="capitalize">{log?.severity}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => setSelectedLog(log)}
                          className="text-primary-orange hover:text-primary-orange/80 transition-colors"
                        >
                          Details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {filteredLogs?.length === 0 && (
            <div className="text-center py-12">
              <Shield className="mx-auto h-12 w-12 text-white-shade-2" />
              <h3 className="mt-2 text-sm font-medium text-white">No audit logs found</h3>
              <p className="mt-1 text-sm text-white-shade-2">
                Try adjusting your filters or search criteria
              </p>
            </div>
          )}
        </div>
      </div>
      </main>

      {/* Detail Side Panel */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center">
          <div className="bg-surface-charcoal rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto border border-charcoal-shade-2">
            <div className="sticky top-0 bg-surface-charcoal border-b border-charcoal-shade-2 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Audit Log Details</h2>
              <button
                onClick={() => setSelectedLog(null)}
                className="text-white-shade-2 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-6">
              {/* Basic Info */}
              <div>
                <h3 className="text-sm font-medium text-white-shade-2 mb-3">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-white-shade-2">Timestamp</p>
                    <p className="text-sm font-medium text-white">{formatTimestamp(selectedLog?.timestamp)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white-shade-2">Event Category</p>
                    <p className="text-sm font-medium text-white">{selectedLog?.eventCategory}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white-shade-2">Action</p>
                    <p className="text-sm font-medium text-white">{selectedLog?.action}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white-shade-2">Severity</p>
                    <div className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${getSeverityColor(selectedLog?.severity)}`}>
                      {getSeverityIcon(selectedLog?.severity)}
                      <span>{selectedLog?.severity}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actor Info */}
              <div>
                <h3 className="text-sm font-medium text-white-shade-2 mb-3">Actor Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-white-shade-2">Name</p>
                    <p className="text-sm font-medium text-white">{selectedLog?.actorName}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white-shade-2">Email</p>
                    <p className="text-sm font-medium text-white">{selectedLog?.actorEmail}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white-shade-2">IP Address</p>
                    <p className="text-sm font-medium text-white font-mono">{selectedLog?.ipAddress}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white-shade-2">Device</p>
                    <p className="text-sm font-medium text-white truncate">{selectedLog?.deviceInfo}</p>
                  </div>
                </div>
              </div>

              {/* Resource Info */}
              <div>
                <h3 className="text-sm font-medium text-white-shade-2 mb-3">Resource Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-white-shade-2">Resource Type</p>
                    <p className="text-sm font-medium text-white">{selectedLog?.resourceType}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white-shade-2">Resource ID</p>
                    <p className="text-sm font-medium text-white font-mono">{selectedLog?.resourceId}</p>
                  </div>
                  {selectedLog?.company && (
                    <>
                      <div>
                        <p className="text-xs text-white-shade-2">Company</p>
                        <p className="text-sm font-medium text-white">{selectedLog?.company}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white-shade-2">Company ID</p>
                        <p className="text-sm font-medium text-white font-mono">{selectedLog?.companyId}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Metadata */}
              <div>
                <h3 className="text-sm font-medium text-white-shade-2 mb-3">Metadata (JSON)</h3>
                <pre className="bg-dark-charcoal border border-charcoal-shade-2 rounded-lg p-4 text-xs overflow-x-auto text-white-shade-1">
                  {JSON.stringify(selectedLog?.metadata, null, 2)}
                </pre>
              </div>

              {/* Before/After Comparison */}
              {selectedLog?.metadata?.before && selectedLog?.metadata?.after && (
                <div>
                  <h3 className="text-sm font-medium text-white-shade-2 mb-3">Change Comparison</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-medium text-red-400 mb-2">Before</p>
                      <pre className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-xs overflow-x-auto text-red-300">
                        {JSON.stringify(selectedLog?.metadata?.before, null, 2)}
                      </pre>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-accent-green mb-2">After</p>
                      <pre className="bg-accent-green/10 border border-accent-green/30 rounded-lg p-3 text-xs overflow-x-auto text-accent-green">
                        {JSON.stringify(selectedLog?.metadata?.after, null, 2)}
                      </pre>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-dark-charcoal border-t border-charcoal-shade-2 px-6 py-4">
              <button
                onClick={() => setSelectedLog(null)}
                className="w-full bg-surface-charcoal hover:bg-charcoal-shade-2 text-white font-medium py-2 px-4 rounded-lg transition-colors border border-charcoal-shade-2"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLoggingSystem;